import { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  TrendingUp,
  TrendingDown,
  PauseCircle,
  ShieldCheck,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  Zap,
  Eye,
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Read-only "what is the system doing with this signal?" view. Shows:
 *   - Header: symbol, direction, score, quality label
 *   - System Status: automation on/off + will-execute verdict
 *   - Trade Plan: entry, SL, TP, risk per trade
 *   - Reason: 2–3 concise bullets
 *   - Live Status: Waiting / Open / Closed (TP/SL)
 *   - Optional: one button to turn automation OFF
 *
 * No manual execution button, no charts. Pure transparency.
 */
export function SignalDetailDialog({ signal, open, onOpenChange, token, onSettingsChanged }) {
  const [settings, setSettings] = useState(null);
  const [linkedTrade, setLinkedTrade] = useState(null);
  const [loading, setLoading] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [livePrice, setLivePrice] = useState(null);

  // Load signal / settings / trade once dialog opens
  useEffect(() => {
    if (!open || !signal || !token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [settingsRes, tradesRes] = await Promise.all([
          fetch(`${API_URL}/api/paper-trading/settings`, { headers }),
          fetch(`${API_URL}/api/paper-trading/trades?limit=200`, { headers }),
        ]);
        if (cancelled) return;
        if (settingsRes.ok) setSettings(await settingsRes.json());
        if (tradesRes.ok) {
          const trades = await tradesRes.json();
          const match = trades.find((t) => t.signal_id === signal.id);
          setLinkedTrade(match || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, signal, token]);

  // Poll the live-price feed AND the trade itself while the dialog is open
  // on an OPEN position, so the user sees the position snapshot update in near
  // real time. Cheap polling (~8s) — both endpoints are already cached.
  useEffect(() => {
    if (!open || !signal || !token) return;
    if (!linkedTrade || linkedTrade.status !== 'open') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [pricesRes, tradesRes] = await Promise.all([
          fetch(`${API_URL}/api/paper-trading/live-prices`),
          fetch(`${API_URL}/api/paper-trading/trades?limit=50`, { headers }),
        ]);
        if (cancelled) return;
        if (pricesRes.ok) {
          const p = await pricesRes.json();
          const sym = String(signal.symbol).toUpperCase();
          if (p.prices && p.prices[sym] != null) setLivePrice(p.prices[sym]);
        }
        if (tradesRes.ok) {
          const trades = await tradesRes.json();
          const match = trades.find((t) => t.signal_id === signal.id);
          if (match) setLinkedTrade(match);
        }
      } catch {
        /* silent */
      }
    };
    tick();
    const t = setInterval(tick, 8000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [open, signal, token, linkedTrade?.status, linkedTrade?.id]);

  const direction = useMemo(() => {
    if (!signal) return 'BUY';
    const raw = String(signal.signal || '').toUpperCase();
    if (raw.includes('SELL')) return 'SELL';
    if (raw.includes('BUY')) return 'BUY';
    return 'BUY';
  }, [signal]);

  const tradePlan = useMemo(() => {
    if (!signal || !settings) return null;
    const entry = Number(signal.price);
    const isSell = direction === 'SELL';
    const stopLoss = isSell ? entry * 1.01 : entry * 0.99;
    const takeProfit = isSell ? entry * 0.98 : entry * 1.02;
    const balance = Number(settings.account_balance ?? 1000);
    const riskPct = Number(settings.risk_percent ?? 1);
    const riskAmount = balance * (riskPct / 100);
    return {
      entry,
      stopLoss,
      takeProfit,
      riskAmount,
      riskPct,
      balance,
    };
  }, [signal, settings, direction]);

  const qualityLabel = useMemo(() => {
    const s = signal?.score ?? 0;
    if (s >= 80) return { text: 'High-quality setup', klass: 'text-success bg-success/10 border-success/30' };
    if (s >= 70) return { text: 'Moderate', klass: 'text-primary bg-primary/10 border-primary/30' };
    return { text: 'Low', klass: 'text-muted-foreground bg-muted/40 border-border/40' };
  }, [signal]);

  const willExecute = useMemo(() => {
    if (!signal || !settings) return { verdict: 'unknown', headline: 'Loading…', sub: '' };
    const threshold =
      (settings.asset_thresholds || {})[String(signal.symbol).toUpperCase()] ??
      settings.min_score_threshold ??
      70;
    if (!settings.enabled) {
      return {
        verdict: 'no',
        headline: 'Not executed — automation OFF',
        sub: 'Turn automation on to let the system trade signals like this one.',
      };
    }
    if (settings.emergency_stop) {
      return {
        verdict: 'no',
        headline: 'Not executed — emergency stop active',
        sub: 'Reset the emergency stop to resume trading.',
      };
    }
    if (settings.signal_mode === 'custom') {
      return {
        verdict: 'no',
        headline: 'Not executed — using your own TradingView alerts',
        sub: 'System signals are ignored while Custom mode is on.',
      };
    }
    if ((signal.score ?? 0) < threshold) {
      return {
        verdict: 'no',
        headline: 'Skipped — signal not strong enough',
        sub: `This one scored ${signal.score}. Your bar is ${threshold}.`,
      };
    }
    return {
      verdict: 'yes',
      headline: 'Trade will be executed automatically',
      sub: 'You don\u2019t need to do anything — the system handles it.',
    };
  }, [signal, settings]);

  const liveStatus = useMemo(() => {
    if (!signal) return null;
    if (linkedTrade) {
      if (linkedTrade.status === 'open') {
        return { label: 'Open — monitoring for TP/SL', klass: 'text-primary', icon: Zap };
      }
      if (linkedTrade.status === 'closed_tp') {
        return { label: 'Closed — Take Profit hit', klass: 'text-success', icon: CheckCircle };
      }
      if (linkedTrade.status === 'closed_sl') {
        return { label: 'Closed — Stop Loss hit', klass: 'text-destructive', icon: XCircle };
      }
      if (linkedTrade.status === 'closed_manual') {
        return { label: 'Closed manually', klass: 'text-muted-foreground', icon: CheckCircle };
      }
    }
    // No trade → either still waiting for automation fan-out, or it was skipped
    if (willExecute.verdict === 'yes') {
      return { label: 'Waiting for entry', klass: 'text-muted-foreground', icon: Clock };
    }
    return { label: 'Not executed', klass: 'text-muted-foreground', icon: PauseCircle };
  }, [signal, linkedTrade, willExecute]);

  const reasonBullets = useMemo(() => {
    if (!signal) return [];
    const bullets = [];
    const s = signal.score ?? 0;
    if (s >= 80) bullets.push(`Strong setup — ${s} out of 100`);
    else if (s >= 70) bullets.push(`Decent setup — ${s} out of 100`);
    else bullets.push(`Weak setup — ${s} out of 100`);

    const sig = String(signal.signal || '').toLowerCase();
    if (sig.includes('pullback')) bullets.push('Pullback pattern spotted');
    else if (sig.includes('breakout')) bullets.push('Breakout pattern spotted');

    if (signal.timeframe) bullets.push(`${signal.symbol} · ${signal.timeframe} chart`);
    return bullets.slice(0, 3);
  }, [signal]);

  // Open-position snapshot — live P&L vs entry, pulled from the most recent
  // price we know about (trade's current_price → live-prices poll → entry).
  const openPosition = useMemo(() => {
    if (!linkedTrade || linkedTrade.status !== 'open') return null;
    const entry = Number(linkedTrade.entry_price);
    const positionSize = Number(linkedTrade.position_size || 1);
    const rawCurrent = livePrice ?? linkedTrade.current_price;
    const hasLive = rawCurrent != null && !Number.isNaN(Number(rawCurrent));
    const current = hasLive ? Number(rawCurrent) : entry;
    const isLong = Number(linkedTrade.take_profit) > entry;
    const pnl = hasLive
      ? (isLong ? current - entry : entry - current) * positionSize
      : 0;
    const pnlPct =
      hasLive && entry > 0
        ? ((isLong ? current - entry : entry - current) / entry) * 100
        : 0;
    return {
      entry,
      current,
      stopLoss: Number(linkedTrade.stop_loss),
      takeProfit: Number(linkedTrade.take_profit),
      maxLoss: Number(linkedTrade.risk_amount || 0),
      pnl: Number.isFinite(pnl) ? pnl : 0,
      pnlPct: Number.isFinite(pnlPct) ? pnlPct : 0,
      isLong,
      hasLive,
    };
  }, [linkedTrade, livePrice]);

  const disableAutomation = async () => {
    if (!token || !settings) return;
    setDisabling(true);
    try {
      const res = await fetch(`${API_URL}/api/paper-trading/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled: false,
          max_trades_per_day: settings.max_trades_per_day,
          emergency_stop: settings.emergency_stop,
          min_score_threshold: settings.min_score_threshold,
        }),
      });
      if (!res.ok) throw new Error('failed');
      toast.success('Automation turned off.');
      setSettings((s) => ({ ...s, enabled: false }));
      onSettingsChanged?.();
    } catch {
      toast.error('Could not turn off automation. Please retry.');
    } finally {
      setDisabling(false);
    }
  };

  if (!signal) return null;

  const fmtPrice = (p) =>
    p >= 100 ? p.toFixed(2) : p >= 10 ? p.toFixed(3) : p.toFixed(5);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-card border-border/40 p-0 overflow-hidden"
        data-testid="signal-detail-dialog"
      >
        {/* Header */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border/30">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="text-xl font-black tracking-tight text-foreground flex items-center gap-2">
                {signal.symbol}
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border ${
                    direction === 'SELL'
                      ? 'bg-destructive/10 text-destructive border-destructive/30'
                      : 'bg-success/10 text-success border-success/30'
                  }`}
                  data-testid="signal-detail-direction"
                >
                  {direction === 'SELL' ? (
                    <TrendingDown className="w-3 h-3" />
                  ) : (
                    <TrendingUp className="w-3 h-3" />
                  )}
                  {direction}
                </span>
              </DialogTitle>
              <p className="text-[11px] text-muted-foreground mt-1">
                {format(new Date(signal.timestamp), 'MMM dd, yyyy HH:mm')} · {signal.timeframe}
              </p>
            </div>
            <div className="text-right">
              <p
                className={`text-3xl font-black font-mono tabular-nums leading-none ${
                  (signal.score ?? 0) >= 80
                    ? 'text-success'
                    : (signal.score ?? 0) >= 70
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`}
                data-testid="signal-detail-score"
              >
                {signal.score}
              </p>
              <span
                className={`mt-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold border ${qualityLabel.klass}`}
                data-testid="signal-detail-quality"
              >
                {qualityLabel.text}
              </span>
            </div>
          </div>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4">
          {/* === OPEN-trade flow: "Trade is now OPEN" rich reassurance state === */}
          {openPosition ? (
            <>
              {/* 1. Top banner — spec §1 */}
              <div
                className="-mx-5 -mt-4 px-5 py-3 border-b border-success/30 bg-success/15 flex items-center gap-2"
                data-testid="signal-detail-open-banner"
              >
                <Zap className="w-4 h-4 text-success shrink-0 animate-pulse" />
                <div className="min-w-0">
                  <p className="text-sm font-bold leading-tight text-success">
                    Trade is now OPEN
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Opened automatically — risk is controlled.
                  </p>
                </div>
              </div>

              {/* 2. Position snapshot — spec §2 */}
              <section data-testid="signal-detail-open-snapshot">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  Your position
                </h3>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
                  <div>
                    <p className="text-[10px] text-muted-foreground">Entry</p>
                    <p className="font-mono font-bold text-foreground">
                      {fmtPrice(openPosition.entry)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      Current
                      {openPosition.hasLive && (
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-success animate-pulse"
                          aria-label="live"
                        />
                      )}
                    </p>
                    <p className="font-mono font-bold text-foreground">
                      {openPosition.hasLive ? fmtPrice(openPosition.current) : '—'}
                    </p>
                  </div>
                </div>
                {/* Soft P&L — only when we have a live price; otherwise noise */}
                {openPosition.hasLive && (
                  <p
                    className={`mt-2 text-[11px] font-mono ${
                      openPosition.pnl >= 0 ? 'text-success/90' : 'text-destructive/80'
                    }`}
                    data-testid="signal-detail-open-pnl"
                  >
                    {openPosition.pnl >= 0 ? '+' : '-'}${Math.abs(openPosition.pnl).toFixed(2)}
                    <span className="ml-1 text-muted-foreground text-[10px] font-normal">
                      ({openPosition.pnl >= 0 ? '+' : ''}
                      {openPosition.pnlPct.toFixed(2)}%)
                    </span>
                  </p>
                )}
              </section>

              {/* 3. Risk is fixed — spec §3 */}
              <section
                className="rounded-md border border-border/40 bg-muted/20 p-3"
                data-testid="signal-detail-risk-fixed"
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <Shield className="w-3.5 h-3.5 text-primary" />
                  <h3 className="text-[11px] font-bold uppercase tracking-wider text-foreground">
                    Risk is fixed
                  </h3>
                </div>
                <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs">
                  <div>
                    <p className="text-[10px] text-muted-foreground">Stop</p>
                    <p className="font-mono font-bold text-destructive">
                      {fmtPrice(openPosition.stopLoss)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Target</p>
                    <p className="font-mono font-bold text-success">
                      {fmtPrice(openPosition.takeProfit)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground">Max loss</p>
                    <p className="font-mono font-bold text-foreground">
                      ${openPosition.maxLoss.toFixed(2)}
                    </p>
                  </div>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  The stop loss is already in place. You can&apos;t lose more than ${openPosition.maxLoss.toFixed(2)}.
                </p>
              </section>

              {/* 4. What happens next — spec §4 */}
              <section data-testid="signal-detail-next-steps">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  What happens next
                </h3>
                <ul className="space-y-1.5 text-[11px] text-foreground">
                  <li className="flex gap-2">
                    <Eye className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                    <span>The system monitors the price continuously.</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle className="w-3.5 h-3.5 text-success shrink-0 mt-0.5" />
                    <span>
                      It closes automatically at your Stop or Target — whichever hits first.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <ShieldCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                    <span>No action needed from you.</span>
                  </li>
                </ul>
              </section>

              {/* 5. Status line — spec §5 */}
              <div
                className="flex items-center gap-2 text-[11px] font-semibold text-primary"
                data-testid="signal-detail-open-status"
              >
                <Zap className="w-3.5 h-3.5 animate-pulse" />
                <span>Monitoring — waiting for TP/SL</span>
              </div>
            </>
          ) : (
            <>
              {/* Prominent decision line — signals without an open trade */}
              <div
                className={`-mx-5 -mt-4 px-5 py-3 border-b border-border/30 flex items-center gap-2 ${
                  willExecute.verdict === 'yes'
                    ? 'bg-success/10'
                    : willExecute.verdict === 'no'
                    ? 'bg-muted/30'
                    : ''
                }`}
                data-testid="signal-detail-decision-line"
              >
                {willExecute.verdict === 'yes' ? (
                  <Zap className="w-4 h-4 text-success shrink-0" />
                ) : (
                  <PauseCircle className="w-4 h-4 text-muted-foreground shrink-0" />
                )}
                <div className="min-w-0">
                  <p
                    className={`text-sm font-bold leading-tight ${
                      willExecute.verdict === 'yes' ? 'text-success' : 'text-foreground'
                    }`}
                  >
                    {willExecute.headline}
                  </p>
                  {willExecute.sub && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">{willExecute.sub}</p>
                  )}
                </div>
              </div>

              {/* Completed trade result — prominent when there's a result */}
              {linkedTrade && linkedTrade.status !== 'open' && liveStatus && (
                <section
                  className={`rounded-md border p-3 ${
                    linkedTrade.status === 'closed_tp'
                      ? 'bg-success/10 border-success/30'
                      : linkedTrade.status === 'closed_sl'
                      ? 'bg-destructive/10 border-destructive/30'
                      : 'bg-muted/30 border-border/40'
                  }`}
                  data-testid="signal-detail-result"
                >
                  <div className="flex items-center gap-2">
                    <liveStatus.icon className={`w-5 h-5 ${liveStatus.klass}`} />
                    <p className={`text-sm font-bold ${liveStatus.klass}`}>
                      {linkedTrade.status === 'closed_tp' && 'Trade completed — Take Profit hit'}
                      {linkedTrade.status === 'closed_sl' && 'Trade completed — Stop Loss hit'}
                      {linkedTrade.status === 'closed_manual' && 'Trade closed manually'}
                    </p>
                  </div>
                  {linkedTrade.pnl != null && (
                    <p
                      className={`mt-1 text-2xl font-black font-mono tabular-nums ${
                        linkedTrade.pnl >= 0 ? 'text-success' : 'text-destructive'
                      }`}
                      data-testid="signal-detail-result-pnl"
                    >
                      {linkedTrade.pnl >= 0 ? '+' : ''}${Math.abs(Number(linkedTrade.pnl)).toFixed(2)}
                      <span className="text-[11px] font-normal text-muted-foreground ml-1.5">
                        {linkedTrade.pnl >= 0 ? 'profit' : 'loss'}
                      </span>
                    </p>
                  )}
                </section>
              )}

              {/* Trade Plan — Buy at / Stop / Target / Risk */}
              {tradePlan && (
                <section data-testid="signal-detail-plan">
                  <h3 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    Trade Plan
                  </h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs">
                    <div>
                      <p className="text-[10px] text-muted-foreground">
                        {direction === 'SELL' ? 'Sell at' : 'Buy at'}
                      </p>
                      <p className="font-mono font-bold text-foreground">
                        {fmtPrice(tradePlan.entry)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Risk</p>
                      <p className="font-mono font-bold text-foreground">
                        ${tradePlan.riskAmount.toFixed(2)}
                        <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">
                          ({tradePlan.riskPct}% of account)
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Stop</p>
                      <p className="font-mono font-bold text-destructive">
                        {fmtPrice(tradePlan.stopLoss)}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground">Target</p>
                      <p className="font-mono font-bold text-success">
                        {fmtPrice(tradePlan.takeProfit)}
                      </p>
                    </div>
                  </div>
                </section>
              )}

              {/* Why — short, human-readable bullets */}
              <section data-testid="signal-detail-reason">
                <h3 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                  Why
                </h3>
                <ul className="space-y-1 text-[11px] text-foreground">
                  {reasonBullets.map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-muted-foreground shrink-0">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* "Waiting for entry" or "Not executed" status when applicable */}
              {liveStatus && !linkedTrade && (
                <section data-testid="signal-detail-live-status">
                  <h3 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    Live Status
                  </h3>
                  <div className={`flex items-center gap-2 text-xs font-semibold ${liveStatus.klass}`}>
                    <liveStatus.icon className="w-4 h-4" />
                    <span>{liveStatus.label}</span>
                  </div>
                </section>
              )}
            </>
          )}

          {/* Keep existing "Turn automation OFF" button — always-on when automation is on */}
          {settings?.enabled && (
            <div className="pt-2 border-t border-border/30">
              <Button
                variant="outline"
                size="sm"
                onClick={disableAutomation}
                disabled={disabling}
                className="w-full text-xs"
                data-testid="signal-detail-disable-automation"
              >
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                Turn automation OFF
              </Button>
            </div>
          )}

          {loading && (
            <p className="text-[10px] text-muted-foreground text-center">Loading…</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
